import { DestroyRef } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { SignalrService } from './signalr.service';

interface JoinConfig<T> {
  readonly signalrService: SignalrService;
  readonly destroyRef: DestroyRef;
  readonly group: string;
  readonly data$: Observable<T>;
  readonly onData: (data: T) => void;
}

interface JoinWithIdConfig<T> extends JoinConfig<T> {
  readonly id: string;
}

interface JoinWithChannelConfig<T> {
  readonly signalrService: SignalrService;
  readonly destroyRef: DestroyRef;
  readonly hubMethod: string;
  readonly id: string;
  readonly channel: string;
  readonly data$: Observable<T>;
  readonly onData: (data: T) => void;
}

interface SubscribeConfig<T> {
  readonly destroyRef: DestroyRef;
  readonly data$: Observable<T>;
  readonly onData: (data: T) => void;
}

export class SignalrGroup<T> {
  private groupName = '';
  private readonly signalrService?: SignalrService;
  private readonly subscriptions: Subscription[] = [];

  private constructor(signalrService?: SignalrService) {
    this.signalrService = signalrService;
  }

  static join<T>(config: JoinConfig<T>): SignalrGroup<T> {
    const instance = new SignalrGroup<T>(config.signalrService);

    const connectSub = config.signalrService.connectState.subscribe({
      next: (connected: boolean) => {
        if (connected) {
          config.signalrService.joinStaticGroup(config.group)?.then((group: string) => {
            instance.groupName = group;
          });
        }
      },
    });
    instance.subscriptions.push(connectSub);

    const dataSub = config.data$.subscribe({ next: config.onData });
    instance.subscriptions.push(dataSub);

    config.destroyRef.onDestroy(() => {
      instance.leave();
      instance.subscriptions.forEach((sub) => sub.unsubscribe());
    });

    return instance;
  }

  static joinWithId<T>(config: JoinWithIdConfig<T>): SignalrGroup<T> {
    const instance = new SignalrGroup<T>(config.signalrService);

    const connectSub = config.signalrService.connectState.subscribe({
      next: (connected: boolean) => {
        if (connected) {
          config.signalrService.joinIdGroup(config.group, config.id)?.then((group: string) => {
            instance.groupName = group;
          });
        }
      },
    });
    instance.subscriptions.push(connectSub);

    const dataSub = config.data$.subscribe({ next: config.onData });
    instance.subscriptions.push(dataSub);

    config.destroyRef.onDestroy(() => {
      instance.leave();
      instance.subscriptions.forEach((sub) => sub.unsubscribe());
    });

    return instance;
  }

  static joinWithChannel<T>(config: JoinWithChannelConfig<T>): SignalrGroup<T> {
    const instance = new SignalrGroup<T>(config.signalrService);

    const connectSub = config.signalrService.connectState.subscribe({
      next: (connected: boolean) => {
        if (connected) {
          config.signalrService
            .joinChannelGroup(config.hubMethod, config.id, config.channel)
            ?.then((group: string) => {
              instance.groupName = group;
            });
        }
      },
    });
    instance.subscriptions.push(connectSub);

    const dataSub = config.data$.subscribe({ next: config.onData });
    instance.subscriptions.push(dataSub);

    config.destroyRef.onDestroy(() => {
      instance.leave();
      instance.subscriptions.forEach((sub) => sub.unsubscribe());
    });

    return instance;
  }

  static subscribe<T>(config: SubscribeConfig<T>): SignalrGroup<T> {
    const instance = new SignalrGroup<T>();

    const dataSub = config.data$.subscribe({ next: config.onData });
    instance.subscriptions.push(dataSub);

    config.destroyRef.onDestroy(() => {
      instance.subscriptions.forEach((sub) => sub.unsubscribe());
    });

    return instance;
  }

  leave(): void {
    if (this.groupName && this.signalrService) {
      this.signalrService.leaveGroup(this.groupName);
      this.groupName = '';
    }
  }
}
